<template>
  <div class="finetune-threshold pb-3">
    <div class="finetune-threshold__body">
      <div v-if="totalSelectableSuggestions > 0" class="finetune-global-select-all mb-2">
        <a-checkbox
          :checked="selectAllChecked"
          :indeterminate="selectAllIndeterminate"
          @change="onSelectAllCheckboxChange"
        >
          Select all
        </a-checkbox>
      </div>

      <template v-if="originDisplayGroups.length">
        <a-collapse bordered class="finetune-threshold__collapse">
          <a-collapse-panel
            v-for="originGroup in paginatedOriginDisplayGroups"
            :key="originGroup.groupKey"
            class="origin-panel"
          >
            <template slot="header">
              <div class="finetune-panel-header">
                <span class="finetune-panel-header__title">
                  {{ originGroup.origin }}
                </span>
                <span class="finetune-panel-header__meta">
                  <a-tag>URLs: {{ originGroup.urlIds.length }}</a-tag>
                  <a-tag>Alerts: {{ originGroup.incidentCount }}</a-tag>
                  <a-tag color="blue">
                    Suggestion Groups: {{ originGroup.groups.length }}
                  </a-tag>
                </span>
              </div>
            </template>

            <a-collapse
              accordion
              bordered
              class="finetune-threshold__group-collapse"
              @change="onPanelChange"
            >
              <a-collapse-panel
                v-for="group in originGroup.groups"
                :key="group.groupKey"
                class="group-panel"
              >
                <template slot="header">
                  <div class="finetune-panel-header">
                    <span class="finetune-panel-header__title">
                      Suggestion Group {{ group.groupIndex + 1 }}
                    </span>
                    <span class="finetune-panel-header__meta">
                      <a-tag>URLs: {{ group.urlIds.length }}</a-tag>
                      <a-tag>Alerts: {{ group.incidentCount }}</a-tag>
                      <a-tag color="blue">
                        Suggestions: {{ group.suggestions.length }}
                      </a-tag>
                    </span>
                  </div>
                </template>
                <div class="finetune-affected-urls mb-2">
                  <div class="text-muted mb-1">
                    Affected URLs
                  </div>
                  <a-tag
                    v-for="affectedUrl in group.affectedUrls"
                    :key="affectedUrl.urlId"
                    class="mb-1"
                    :title="affectedUrl.url"
                  >
                    {{ affectedUrl.url }}
                  </a-tag>
                </div>
                <div class="table-analytics finetune-content-analytics-table">
                  <a-table
                    :rowKey="record => record.rowKey"
                    :columns="FinetuneContentAnalyticsColumns"
                    :data-source="rowsForGroup(group)"
                    :pagination="false"
                    :row-selection="rowSelectionForGroup(group)"
                  >
                    <template slot="index" slot-scope="text">
                      {{ text }}
                    </template>
                    <template slot="description" slot-scope="text, record">
                      <div>
                        <span>
                          {{ text }}
                          <a-tooltip v-if="record.modeDifference != null" placement="top">
                            <template slot="title">
                              <span class="fs-10">
                                Mode difference: {{ record.modeDifference }}
                              </span>
                            </template>
                            <a-icon class="explain ml-1" type="info-circle" theme="filled" />
                          </a-tooltip>
                        </span>
                        <div v-if="record.sampleStatsLabel" class="mt-1">
                          <a-tooltip placement="top">
                            <template slot="title">
                              <span class="fs-10">
                                Sample count / URL alerts
                              </span>
                            </template>
                            <a-tag color="blue" class="m-0">
                              {{ record.sampleStatsLabel }}
                            </a-tag>
                          </a-tooltip>
                        </div>
                      </div>
                    </template>
                    <template slot="baselineValue" slot-scope="text">
                      <span>{{ text }}</span>
                    </template>
                    <template slot="currentThreshold" slot-scope="text">
                      <span>{{ text }}</span>
                    </template>
                    <template slot="newThreshold" slot-scope="text, record">
                      <a-input
                        type="text"
                        :value="String(record.newThresholdValue)"
                        :disabled="!isSuggestionSelected(record.rowKey)"
                        @change="(e) => onNewThresholdChange(record, e.target.value)"
                      />
                    </template>
                  </a-table>
                  <div class="finetune-panel-apply">
                    <a-button
                      type="primary"
                      :loading="isApplyingThresholds"
                      :disabled="selectedCountForGroup(group) === 0"
                      @click="onApplyGroup(group)"
                    >
                      Apply {{ selectedCountForGroup(group) }} suggestion(s)
                    </a-button>
                  </div>
                </div>
                <RelatedThresholdApply
                  v-if="selectedCountForGroup(group) > 0 && getGroupDomain(group)"
                  class="finetune-related-apply"
                  :domain="getGroupDomain(group)"
                  :tags="getRelatedTagsForGroup(group)"
                  :selected-tag-id="getSelectedRelatedTagId(group)"
                  :loading-tags="isLoadingRelatedTags(group)"
                  :applying="isApplyingThresholds"
                  @update:selected-tag-id="setSelectedRelatedTagId(group, $event)"
                  @apply-domain="onApplyFloorForDomain(group)"
                  @apply-tag="onApplyFloorForTag(group, $event)"
                />
              </a-collapse-panel>
            </a-collapse>
          </a-collapse-panel>
        </a-collapse>

        <div v-if="originDisplayGroups.length > 5" class="finetune-origin-pagination mt-3">
          <PaginationBar
            :current="originPageCurrent"
            :pageSize="originPageSize"
            :total="originDisplayGroups.length"
            storageKey="autoFinetuneFqdnPageSize"
            @change="onOriginPaginationChange"
          />
        </div>
      </template>

      <div v-else class="text-center text-muted py-4">
        No URLs with suggestions.
      </div>
    </div>

    <div class="finetune-threshold__footer border-top bg-white py-3 mt-3">
      <div class="d-flex align-items-center justify-content-between flex-wrap">
        <span class="text-muted mb-1 mb-md-0">
          {{ selectedCount }} suggestion(s) selected
        </span>
        <a-button type="primary" :loading="isApplyingThresholds" :disabled="selectedCount === 0" @click="onApply">
          Apply {{ selectedCount }} suggestion(s)
        </a-button>
      </div>
    </div>
  </div>
</template>

<script>
import RelatedThresholdApply from '@shared/components/RelatedThresholdApply.vue';
import WebpageService from '@modules/Webpages/services/webpage.service';
import { FinetuneContentAnalyticsColumns } from '../columnsTable/FinetuneContentAnalyticsColumns';
import thresholdApplyMixin from '@shared/mixins/thresholdApply.mixin';
import {
  countThresholdEntries,
  getFinetuneElementDescription,
  normalizeRelatedTags,
  parseThresholdInput,
  THRESHOLD_ELEMENT_TO_ATTRIBUTE
} from '@shared/utils/finetune';
import { extractHostname } from '@shared/utils/url';
import { groupThresholdSuggestionsByOrigin } from '@modules/AutoFinetune/helpers/thresholdSuggestionGroups';

export default {
  name: 'FinetuneSuggestionDetailThreshold',
  components: {
    RelatedThresholdApply
  },
  mixins: [thresholdApplyMixin],
  props: {
    detail: {
      type: Object,
      default: () => ({
        urls: []
      })
    }
  },
  data() {
    return {
      FinetuneContentAnalyticsColumns,
      selectedSuggestions: {},
      suggestedThresholdEdits: {},
      relatedTagsByGroup: {},
      relatedTagsLoading: {},
      selectedRelatedTagIds: {},
      originPageCurrent: 1,
      originPageSize:
        Number(window.localStorage.getItem('autoFinetuneFqdnPageSize')) || 5
    };
  },
  computed: {
    suggestionGroups() {
      return groupThresholdSuggestionsByOrigin(this.detail.urls);
    },
    displayGroups() {
      return this.suggestionGroups.reduce((groups, originGroup) => {
        const groupsByTargetUrls = new Map();

        originGroup.suggestions.forEach((suggestion) => {
          const urlIds = [...suggestion.targetUrlIds].sort(
            (first, second) => first - second
          );
          const targetKey = JSON.stringify(urlIds);
          if (!groupsByTargetUrls.has(targetKey)) {
            groupsByTargetUrls.set(targetKey, {
              groupKey: `${originGroup.origin}::${targetKey}`,
              origin: originGroup.origin,
              urlIds,
              affectedUrls: suggestion.affectedUrls,
              incidentCount: suggestion.incidentCount || 0,
              suggestions: []
            });
          }
          groupsByTargetUrls.get(targetKey).suggestions.push(suggestion);
        });

        Array.from(groupsByTargetUrls.values()).forEach((group, groupIndex) => {
          groups.push({
            ...group,
            groupIndex
          });
        });
        return groups;
      }, []);
    },
    originDisplayGroups() {
      return this.suggestionGroups.map((originGroup) => ({
        groupKey: `fqdn::${originGroup.groupKey}`,
        origin: originGroup.origin,
        urlIds: originGroup.urlIds,
        incidentCount: originGroup.incidentCount,
        groups: this.displayGroups.filter(
          (group) => group.origin === originGroup.origin
        )
      }));
    },
    paginatedOriginDisplayGroups() {
      const start = (this.originPageCurrent - 1) * this.originPageSize;
      return this.originDisplayGroups.slice(start, start + this.originPageSize);
    },
    selectedCount() {
      let n = 0;
      this.displayGroups.forEach((group) => {
        group.suggestions.forEach((suggestion) => {
          if (this.isSuggestionSelected(suggestion.rowKey)) {
            n += 1;
          }
        });
      });
      return n;
    },
    totalSelectableSuggestions() {
      return this.displayGroups.reduce(
        (total, group) => total + group.suggestions.length,
        0
      );
    },
    selectAllChecked() {
      const total = this.totalSelectableSuggestions;
      if (total === 0) return false;
      return this.selectedCount === total;
    },
    selectAllIndeterminate() {
      const total = this.totalSelectableSuggestions;
      if (total === 0) return false;
      if (this.selectAllChecked) return false;
      return this.selectedCount > 0;
    }
  },
  watch: {
    detail: {
      immediate: true,
      handler() {
        this.suggestedThresholdEdits = {};
        this.relatedTagsByGroup = {};
        this.relatedTagsLoading = {};
        this.selectedRelatedTagIds = {};
        this.originPageCurrent = 1;
        this.initializeSelection();
      }
    }
  },
  methods: {
    onOriginPaginationChange(pagination) {
      this.originPageCurrent = pagination.current;
      this.originPageSize = pagination.pageSize;
    },
    rowsForGroup(group) {
      return group.suggestions.map((suggestion, index) => {
        const suggested =
          this.suggestedThresholdEdits[suggestion.rowKey] != null
            ? this.suggestedThresholdEdits[suggestion.rowKey]
            : suggestion.suggested_threshold;

        const sampleCount = suggestion.sampleCount;
        const incidentCount = suggestion.incidentCount;
        const sampleStatsLabel =
          sampleCount != null && incidentCount != null
            ? `${sampleCount} / ${incidentCount} alerts`
            : incidentCount != null
              ? `${incidentCount} alerts`
              : sampleCount != null
                ? `${sampleCount} samples`
                : '';

        return {
          rowKey: suggestion.rowKey,
          groupKey: group.groupKey,
          index: index + 1,
          description: getFinetuneElementDescription(suggestion.element),
          baselineValue: suggestion.baseline_value,
          sampleStatsLabel,
          currentThreshold: suggestion.current_threshold,
          newThresholdValue: suggested,
          modeDifference: suggestion.mode_difference,
          element: suggestion.element
        };
      });
    },
    rowSelectionForGroup(group) {
      return {
        selectedRowKeys: group.suggestions
          .filter((suggestion) => this.isSuggestionSelected(suggestion.rowKey))
          .map((suggestion) => suggestion.rowKey),
        onChange: (keys) => this.onGroupRowSelectionChange(group, keys)
      };
    },
    onGroupRowSelectionChange(group, selectedKeys) {
      const set = new Set(selectedKeys);
      group.suggestions.forEach((suggestion) => {
        this.$set(
          this.selectedSuggestions,
          suggestion.rowKey,
          set.has(suggestion.rowKey)
        );
      });
    },
    initializeSelection() {
      const selected = {};
      this.displayGroups.forEach((group) => {
        group.suggestions.forEach((suggestion) => {
          selected[suggestion.rowKey] =
            !this.isSuggestionMoreThanHalfBaseline(suggestion);
        });
      });
      this.selectedSuggestions = selected;
    },
    isSuggestionMoreThanHalfBaseline(suggestion) {
      const suggestedThreshold = Number(suggestion.suggested_threshold);
      if (!Number.isFinite(suggestedThreshold)) return false;

      const affectedUrls = Array.isArray(suggestion.affectedUrls)
        ? suggestion.affectedUrls
        : [];
      const baselineValues = affectedUrls.length
        ? affectedUrls.map((affectedUrl) => affectedUrl.baselineValue)
        : [suggestion.baseline_value];

      return baselineValues.some((baselineValue) => {
        const baseline = Number(baselineValue);
        return Number.isFinite(baseline) && suggestedThreshold > baseline / 2;
      });
    },
    isSuggestionSelected(rowKey) {
      return this.selectedSuggestions[rowKey] !== false;
    },
    onSelectAllCheckboxChange(e) {
      const checked = e.target.checked;
      this.displayGroups.forEach((group) => {
        group.suggestions.forEach((suggestion) => {
          this.$set(this.selectedSuggestions, suggestion.rowKey, checked);
        });
      });
    },
    selectedCountForGroup(group) {
      return group.suggestions.filter((suggestion) =>
        this.isSuggestionSelected(suggestion.rowKey)
      ).length;
    },
    onNewThresholdChange(record, value) {
      this.$set(this.suggestedThresholdEdits, record.rowKey, value);
    },
    getGroupDomain(group) {
      return extractHostname(group.origin) || '';
    },
    getRelatedTagsForGroup(group) {
      return this.relatedTagsByGroup[group.groupKey] || [];
    },
    getSelectedRelatedTagId(group) {
      const selectedTagId = this.selectedRelatedTagIds[group.groupKey];
      return selectedTagId == null ? null : selectedTagId;
    },
    setSelectedRelatedTagId(group, tagId) {
      this.$set(this.selectedRelatedTagIds, group.groupKey, tagId);
    },
    isLoadingRelatedTags(group) {
      return this.relatedTagsLoading[group.groupKey] === true;
    },
    onPanelChange(activeKey) {
      const groupKey = Array.isArray(activeKey) ? activeKey[0] : activeKey;
      if (!groupKey) return;
      const group = this.displayGroups.find((item) => item.groupKey === groupKey);
      if (group) this.loadRelatedTagsForGroup(group);
    },
    async loadRelatedTagsForGroup(group) {
      if (
        Object.prototype.hasOwnProperty.call(this.relatedTagsByGroup, group.groupKey) ||
        this.isLoadingRelatedTags(group)
      ) {
        return;
      }

      const representativeUrlId = group.urlIds[0];
      if (representativeUrlId == null) {
        this.$set(this.relatedTagsByGroup, group.groupKey, []);
        return;
      }

      this.$set(this.relatedTagsLoading, group.groupKey, true);
      try {
        const webpage = await WebpageService.get(representativeUrlId);
        const tags = normalizeRelatedTags(webpage && webpage.tags);
        this.$set(this.relatedTagsByGroup, group.groupKey, tags);
        if (tags.length > 1) {
          this.$set(this.selectedRelatedTagIds, group.groupKey, tags[0].id);
        }
      } catch (_) {
        this.$set(this.relatedTagsByGroup, group.groupKey, []);
      } finally {
        this.$set(this.relatedTagsLoading, group.groupKey, false);
      }
    },
    buildUrlsPayloadForGroups(groups) {
      const thresholdsByUrl = {};

      groups.forEach((group) => {
        group.suggestions.forEach((suggestion) => {
          if (!this.isSuggestionSelected(suggestion.rowKey)) return;
          const attr = THRESHOLD_ELEMENT_TO_ATTRIBUTE[suggestion.element];
          if (!attr) return;
          const raw =
            this.suggestedThresholdEdits[suggestion.rowKey] != null
              ? this.suggestedThresholdEdits[suggestion.rowKey]
              : suggestion.suggested_threshold;
          const value = parseThresholdInput(raw);
          if (value == null) return;

          suggestion.targetUrlIds.forEach((urlId) => {
            if (!thresholdsByUrl[urlId]) thresholdsByUrl[urlId] = {};
            thresholdsByUrl[urlId][attr] = Math.abs(value);
          });
        });
      });

      return Object.keys(thresholdsByUrl).map((urlId) => ({
        url_id: Number(urlId),
        thresholds: thresholdsByUrl[urlId]
      }));
    },
    buildSelectedThresholdsMapForGroup(group) {
      return group.suggestions.reduce((thresholds, suggestion) => {
        if (!this.isSuggestionSelected(suggestion.rowKey)) return thresholds;
        const attribute = THRESHOLD_ELEMENT_TO_ATTRIBUTE[suggestion.element];
        if (!attribute) return thresholds;
        const raw =
          this.suggestedThresholdEdits[suggestion.rowKey] != null
            ? this.suggestedThresholdEdits[suggestion.rowKey]
            : suggestion.suggested_threshold;
        const value = parseThresholdInput(raw);
        if (value != null) thresholds[attribute] = Math.abs(value);
        return thresholds;
      }, {});
    },
    onApply() {
      const urlsPayload = this.buildUrlsPayloadForGroups(this.suggestionGroups);
      const count = countThresholdEntries(urlsPayload);
      if (!count) {
        this.$message.warning(
          'No valid numeric thresholds to apply. Check that selected rows have valid numbers.'
        );
        return;
      }
      this.$confirm({
        title: `Apply ${count} threshold suggestion(s)?`,
        okText: 'Apply',
        cancelText: 'Cancel',
        onOk: () => this.runApplyThresholds(urlsPayload)
      });
    },
    onApplyGroup(group) {
      const urlsPayload = this.buildUrlsPayloadForGroups([group]);
      const count = countThresholdEntries(urlsPayload);
      if (!count) {
        this.$message.warning(
          'No valid numeric thresholds to apply. Check that selected rows have valid numbers.'
        );
        return;
      }
      this.$confirm({
        title: `Apply ${count} threshold update(s) across ${urlsPayload.length} URL(s)?`,
        okText: 'Apply',
        cancelText: 'Cancel',
        onOk: () => this.runApplyThresholds(urlsPayload)
      });
    },
    onApplyFloorForDomain(group) {
      const thresholds = this.buildSelectedThresholdsMapForGroup(group);
      const count = Object.keys(thresholds).length;
      const domain = this.getGroupDomain(group);
      if (!count || !domain) {
        this.$message.warning('No valid numeric thresholds to apply, or domain is missing.');
        return;
      }
      this.$confirm({
        title: `Apply ${count} minimum threshold(s) to all URLs matching domain ${domain}? Existing higher values will remain unchanged.`,
        okText: 'Apply',
        cancelText: 'Cancel',
        onOk: () => this.runApplyThresholdsByTarget({ domain }, thresholds)
      });
    },
    onApplyFloorForTag(group, tagId) {
      const thresholds = this.buildSelectedThresholdsMapForGroup(group);
      const count = Object.keys(thresholds).length;
      if (!count || tagId == null) {
        this.$message.warning('No valid numeric thresholds or tag to apply.');
        return;
      }
      const tag = this.getRelatedTagsForGroup(group).find(
        (item) => String(item.id) === String(tagId)
      );
      const tagLabel = (tag && tag.name) || `tag ${tagId}`;
      this.$confirm({
        title: `Apply ${count} minimum threshold(s) to all URLs matching tag ${tagLabel}? Existing higher values will remain unchanged.`,
        okText: 'Apply',
        cancelText: 'Cancel',
        onOk: () =>
          this.runApplyThresholdsByTarget(
            { tag_id: Number(tagId) || tagId },
            thresholds
          )
      });
    }
  }
};
</script>

<style lang="scss" scoped>
$finetune-detail-padding-x: 20px;

.finetune-threshold__body {
  padding-left: $finetune-detail-padding-x;
  padding-right: $finetune-detail-padding-x;
}

.finetune-threshold__footer {
  padding-left: $finetune-detail-padding-x;
  padding-right: $finetune-detail-padding-x;
}

.finetune-global-select-all {
  font-weight: 500;
}

.finetune-threshold__collapse {
  ::v-deep .ant-collapse-content-box {
    padding: 12px 0 0;
  }
}

.finetune-threshold__group-collapse {
  margin: 0 12px 12px;
}

.finetune-panel-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  width: 100%;
  padding-right: 8px;

  &__title {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__meta {
    flex-shrink: 0;
  }
}

.finetune-panel-apply {
  margin-top: 12px;
  padding: 0 16px 12px;
  text-align: right;
}

.finetune-affected-urls {
  max-height: 120px;
  overflow-y: auto;
  padding: 10px 12px;
}

.finetune-content-analytics-table {
  overflow-x: auto;
}

.finetune-related-apply {
  margin: 0 16px 16px;
}

.table-analytics {
  ::v-deep .ant-table-thead {
    font-weight: 500;

    tr th {
      background: #fff;
    }
  }
}

.explain {
  color: rgba(0, 0, 0, 0.45);
}
</style>
